import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
Box,
Typography,
Container,
Card,
CardHeader,
CardContent,
Divider,
Chip,
Avatar,
List,
ListItem,
ListItemText,
ListItemAvatar,
useMediaQuery,
useTheme,
Slide,
Fade,
Grow,
Accordion,
AccordionSummary,
AccordionDetails,
Skeleton,
Alert,
Paper,
Grid,
TextField,
InputAdornment,
Button,
Stack,
MenuItem,
Select,
FormControl,
InputLabel,
IconButton,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
CircularProgress,
alpha
} from '@mui/material';
import {
Category as CategoryIcon,
ExpandMore as ExpandMoreIcon,
Description as DescriptionIcon,
ViewModule,
Search,
ViewList,
Sort,
FilterList,
Refresh,
Add as AddIcon,
Person as PersonIcon,
DateRange as DateRangeIcon,
CheckCircle as CheckCircleIcon,
Cancel as CancelIcon,
Delete as DeleteIcon,
Edit as EditIcon,
FiberManualRecord,
Subject,
Close as CloseIcon,
Science,
Visibility
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import NavigationMenu from '../navigation/NavigationMenu';
import { APIAreasStore, APIcategoriesTheme } from '../../utilities/APIS';
import axios from 'axios';
import { headers } from '../../utilities/Env';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../utilities/Text';
import useCustomToast from '../../hooks/useToast';
import LoadingState from '../../components/LoadingState';

const ResearchThemes = () => {
    const [researchAreasData, setResearchAreasData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedTheme, setExpandedTheme] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [sortOrder, setSortOrder] = useState('asc');
    
    // Dialog state
    const [openDialog, setOpenDialog] = useState(false);
    const [themeName, setThemeName] = useState('');
    const [descriptionInputs, setDescriptionInputs] = useState([{ data: '' }]);
    const [submitting, setSubmitting] = useState(false);
    const [dialogErrors, setDialogErrors] = useState({});
    
    // Get logged in user
    const [loggedInUser, setLoggedInUser] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    const navigate = useNavigate();
    const theme = useTheme();
    const showToast = useCustomToast();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        // Check if user is logged in
        const user = sessionStorage.getItem('reseacher');
        if (user) {
            const parsedUser = JSON.parse(user);
            setLoggedInUser(parsedUser);
            setIsLoggedIn(parsedUser.loggedIn === true);
        }
        fetchData();
    }, []);

    useEffect(() => {
        filterAndSortData();
    }, [researchAreasData, searchTerm, sortOrder]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const [themesRes] = await Promise.all([
                axios.get(APIcategoriesTheme, { headers }),
            ]);

            const res = Array.isArray(themesRes.data.data) ? themesRes.data.data.reverse() : [];
            setResearchAreasData(res);
            showToast("Success", TEXT_DATA_LOADED, "success");
        } catch (error) {
            let errorMessage = "Failed to load data";

            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            }
            else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }

            setError(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const filterAndSortData = () => {
        let filtered = researchAreasData;

        if (searchTerm) {
            filtered = filtered.filter(theme =>
                theme.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                theme.creator?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        filtered = [...filtered].sort((a, b) => {
            let aValue = a.name?.toLowerCase() || '';
            let bValue = b.name?.toLowerCase() || '';

            if (sortOrder === 'asc') {
                if (aValue < bValue) return -1;
                if (aValue > bValue) return 1;
                return 0;
            } else {
                if (aValue > bValue) return -1;
                if (aValue < bValue) return 1;
                return 0;
            }
        });

        setFilteredData(filtered);
    };

    const handleThemeClick = (themeId) => {
        setExpandedTheme(expandedTheme === themeId ? null : themeId);
    };

    const handleOpenDialog = () => {
        setThemeName('');
        setDescriptionInputs([{ data: '' }]);
        setDialogErrors({});
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setThemeName('');
        setDescriptionInputs([{ data: '' }]);
        setDialogErrors({});
    };

    const handleAddDescription = () => {
        setDescriptionInputs([...descriptionInputs, { data: '' }]);
    };

    const handleRemoveDescription = (index) => {
        if (descriptionInputs.length > 1) {
            const newInputs = descriptionInputs.filter((_, i) => i !== index);
            setDescriptionInputs(newInputs);
        }
    };

    const handleDescriptionChange = (index, value) => {
        const newInputs = [...descriptionInputs];
        newInputs[index] = { data: value };
        setDescriptionInputs(newInputs);
        
        if (dialogErrors.description) {
            setDialogErrors(prev => ({ ...prev, description: '' }));
        }
    };

    const validateDialogForm = () => {
        const newErrors = {};

        if (!themeName.trim()) {
            newErrors.themeName = 'Research theme name is required';
        } else if (themeName.trim().length < 2) {
            newErrors.themeName = 'Research theme name must be at least 2 characters';
        } else if (themeName.trim().length > 100) {
            newErrors.themeName = 'Research theme name must be less than 100 characters';
        }

        const validDescriptions = descriptionInputs.filter(input => 
            input.data && input.data.trim().length > 0
        );
        if (validDescriptions.length === 0) {
            newErrors.description = 'At least one description paragraph is required';
        }

        setDialogErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmitTheme = async () => {
        if (!validateDialogForm()) {
            showToast('Validation Error', 'Please fix the form errors', 'error');
            return;
        }

        setSubmitting(true);

        try {
            const formData = new FormData();
            formData.append('name', themeName.trim());
            
            const validDescriptions = descriptionInputs.filter(input => 
                input.data && input.data.trim().length > 0
            );
            formData.append('description', JSON.stringify(validDescriptions));
            
            // Add creator if logged in
            if (loggedInUser) {
                formData.append('creator', loggedInUser.name);
            }

            const response = await axios.post(APIAreasStore, formData, { headers });
            const result = response.data;
            
            if (result.status === "success") {
                showToast("Success", "Research theme added successfully!", "success");
                handleCloseDialog();
                fetchData();
            } else {
                showToast("Error", result.message || "Operation failed", "error");
            }
        } catch (error) {
            handleApiError(error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleApiError = (error) => {
        if (error.response) {
            if (error.response.status === 422) {
                const errors = error.response.data.errors;
                let errorMessages = [];
                for (const field in errors) {
                    errorMessages.push(...errors[field]);
                }
                showToast("Validation Error", errorMessages.join("\n"), "error");
            } else {
                showToast("Error", error.response.data.message || "An error occurred", "error");
            }
        } else if (error.code === "ERR_NETWORK") {
            showToast("Connection Error", TEXT_CONNECTION_ERROR, "error");
        } else {
            showToast("Error", "An unexpected error occurred", "error");
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        } catch (error) {
            return 'Invalid Date';
        }
    };

    const displayNestedData = (data) => {
        try {
            const parsedData = JSON.parse(data);
            return (
                <List dense>
                    {parsedData.map((item, index) => (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                            <ListItemAvatar>
                                <Avatar sx={{ bgcolor: '#074900', width: 24, height: 24 }}>
                                    <DescriptionIcon sx={{ fontSize: 14 }} />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={item.data}
                                primaryTypographyProps={{ variant: 'body2' }}
                            />
                        </ListItem>
                    ))}
                </List>
            );
        } catch (err) {
            return (
                <Typography variant="body2" color="error">
                    Error parsing theme description
                </Typography>
            );
        }
    };

    const formatDescription = (description) => {
        if (!description) return [];
        try {
            const parsed = JSON.parse(description);
            if (Array.isArray(parsed)) {
                return parsed.map(item => item.data || item);
            }
            return [description];
        } catch {
            return [description];
        }
    };

    const DescriptionBulletList = ({ descriptions, maxItems = null, showExpandable = false, areaId = null, expandedState = null, onToggle = null }) => {
        const itemsToShow = maxItems ? descriptions.slice(0, maxItems) : descriptions;
        const hasMore = showExpandable && descriptions.length > maxItems;
        const isExpanded = expandedState && areaId ? expandedState[areaId] : false;

        return (
            <List dense sx={{ py: 0 }}>
                {itemsToShow.map((desc, index) => (
                    <ListItem key={index} sx={{ py: 0.5, pl: 0 }}>
                        <ListItemAvatar sx={{ minWidth: 32 }}>
                            <FiberManualRecord 
                                sx={{ 
                                    fontSize: '8px', 
                                    color: 'primary.main',
                                }} 
                            />
                        </ListItemAvatar>
                        <ListItemText
                            primary={
                                <Typography variant="body2" color="text.primary">
                                    {desc}
                                </Typography>
                            }
                        />
                    </ListItem>
                ))}
                {hasMore && !isExpanded && (
                    <ListItem sx={{ py: 0.5, pl: 0 }}>
                        <ListItemAvatar sx={{ minWidth: 32 }}>
                            <Subject sx={{ fontSize: '16px', color: 'primary.main' }} />
                        </ListItemAvatar>
                        <ListItemText
                            primary={
                                <Typography 
                                    variant="body2" 
                                    color="primary" 
                                    sx={{ 
                                        cursor: 'pointer',
                                        '&:hover': { textDecoration: 'underline' }
                                    }}
                                    onClick={() => onToggle && onToggle(areaId)}
                                >
                                    +{descriptions.length - maxItems} more points
                                </Typography>
                            }
                        />
                    </ListItem>
                )}
                {hasMore && isExpanded && (
                    <>
                        {descriptions.slice(maxItems).map((desc, index) => (
                            <ListItem key={index + maxItems} sx={{ py: 0.5, pl: 0 }}>
                                <ListItemAvatar sx={{ minWidth: 32 }}>
                                    <FiberManualRecord 
                                        sx={{ 
                                            fontSize: '8px', 
                                            color: 'primary.main',
                                        }} 
                                    />
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Typography variant="body2" color="text.primary">
                                            {desc}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        ))}
                        <ListItem sx={{ py: 0.5, pl: 0 }}>
                            <ListItemAvatar sx={{ minWidth: 32 }}>
                                <Subject sx={{ fontSize: '16px', color: 'primary.main' }} />
                            </ListItemAvatar>
                            <ListItemText
                                primary={
                                    <Typography 
                                        variant="body2" 
                                        color="primary" 
                                        sx={{ 
                                            cursor: 'pointer',
                                            '&:hover': { textDecoration: 'underline' }
                                        }}
                                        onClick={() => onToggle && onToggle(areaId)}
                                    >
                                        Show less
                                    </Typography>
                                }
                            />
                        </ListItem>
                    </>
                )}
            </List>
        );
    };

    if (error) {
        return (
            <Box sx={{ display: 'flex', minHeight: '100vh' }}>
                <NavigationMenu />
                <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                    <Alert severity="error" sx={{ mt: 3 }}>
                        <Typography variant="h6">{error.message}</Typography>
                        <Typography variant="body2">{error.details}</Typography>
                    </Alert>
                </Box>
            </Box>
        );
    }

    if (loading) {
        return (
            <>
                <NavigationMenu />
                <LoadingState/>
            </>
        );
    }

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <NavigationMenu />
            <Box component="main" sx={{ flexGrow: 1, marginTop: 10, p: isMobile ? 1 : 3 }}>
                <Container maxWidth="lg">
                    {/* Header Section */}
                    <Slide direction="down" in={true} mountOnEnter unmountOnExit>
                        <Box sx={{
                            mb: 3,
                            p: 3,
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2
                        }}>
                            <Box>
                                <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Research Areas
                                </Typography>
                                <Typography variant="subtitle1">
                                    Explore our research focus areas
                                </Typography>
                            </Box>
                            {isLoggedIn && (
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleOpenDialog}
                                    sx={{
                                        bgcolor: 'white',
                                        color: '#667eea',
                                        '&:hover': {
                                            bgcolor: '#f0f0f0',
                                            transform: 'translateY(-2px)'
                                        },
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    Add Research Area
                                </Button>
                            )}
                        </Box>
                    </Slide>

                    {/* Search and Filter Section */}
                    <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={5}>
                                <TextField
                                    fullWidth
                                    placeholder="Search by name or creator..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search color="primary" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchTerm && (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setSearchTerm('')}>
                                                    <CloseIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Sort Order</InputLabel>
                                    <Select
                                        value={sortOrder}
                                        label="Sort Order"
                                        onChange={(e) => setSortOrder(e.target.value)}
                                    >
                                        <MenuItem value="asc">A to Z</MenuItem>
                                        <MenuItem value="desc">Z to A</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <Button
                                    variant="outlined"
                                    onClick={fetchData}
                                    startIcon={<Refresh />}
                                    fullWidth
                                    sx={{ height: '56px' }}
                                >
                                    Refresh
                                </Button>
                            </Grid>
                        </Grid>
                        <br />
                        {/* Results Count */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                            <Typography variant="h6" color="text.secondary">
                                Showing {filteredData.length} of {researchAreasData.length} Research Areas
                            </Typography>
                            {searchTerm && (
                                <Chip 
                                    label={`Search: "${searchTerm}"`} 
                                    onDelete={() => setSearchTerm('')}
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                        </Box>
                    </Paper>

                    {/* Research Areas List */}
                    {!loading && (
                        <Fade in={!loading}>
                            <Box>
                                {filteredData.length === 0 ? (
                                    <Paper elevation={3} sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
                                        <CategoryIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                                        <Typography variant="h5" color="text.secondary" gutterBottom>
                                            No Research Areas Found
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                            {searchTerm ? 'Try adjusting your search criteria' : 'No areas available'}
                                        </Typography>
                                        {!searchTerm && isLoggedIn && (
                                            <Button
                                                variant="contained"
                                                startIcon={<AddIcon />}
                                                onClick={handleOpenDialog}
                                            >
                                                Add Your First Research Theme
                                            </Button>
                                        )}
                                    </Paper>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {filteredData.map((theme, index) => {
                                            const descriptions = formatDescription(theme.description);
                                            return (
                                                <motion.div
                                                    key={theme.id}
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: index * 0.1 }}
                                                >
                                                    <Accordion
                                                        expanded={expandedTheme === theme.id}
                                                        onChange={() => handleThemeClick(theme.id)}
                                                        sx={{
                                                            boxShadow: 3,
                                                            '&:hover': {
                                                                boxShadow: 6
                                                            },
                                                            transition: 'box-shadow 0.3s ease',
                                                            borderRadius: 2,
                                                            overflow: 'hidden'
                                                        }}
                                                    >
                                                        <AccordionSummary
                                                            expandIcon={<ExpandMoreIcon />}
                                                            sx={{
                                                                backgroundColor: expandedTheme === theme.id
                                                                    ? alpha('#667eea', 0.1)
                                                                    : '#ffffff',
                                                                '&:hover': {
                                                                    backgroundColor: alpha('#667eea', 0.05)
                                                                }
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, flexWrap: 'wrap' }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                    <Avatar sx={{ bgcolor: '#667eea', width: 56, height: 56 }}>
                                                                        {index + 1}
                                                                    </Avatar>
                                                                    <Box>
                                                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                                            {theme.name}
                                                                        </Typography>
                                                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
                                                                            {theme.creator && (
                                                                                <Chip
                                                                                    size="small"
                                                                                    icon={<PersonIcon fontSize="small" />}
                                                                                    label={`Creator: ${theme.creator || 'Ria Admin'}`}
                                                                                    variant="outlined"
                                                                                    sx={{ borderRadius: 2 }}
                                                                                />
                                                                            )}
                                                                            {theme.created_at && (
                                                                                <Chip
                                                                                    size="small"
                                                                                    icon={<DateRangeIcon fontSize="small" />}
                                                                                    label={`Added: ${formatDate(theme.created_at)}`}
                                                                                    variant="outlined"
                                                                                    sx={{ borderRadius: 2 }}
                                                                                />
                                                                            )}
                                                                            <Chip
                                                                                label="Active"
                                                                                color="success"
                                                                                size="small"
                                                                                icon={<CheckCircleIcon fontSize="small" />}
                                                                                sx={{ borderRadius: 2 }}
                                                                            />
                                                                        </Stack>
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        </AccordionSummary>
                                                        <AccordionDetails>
                                                            <Divider sx={{ mb: 2 }} />
                                                            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
                                                                Description:
                                                            </Typography>
                                                            {theme.description ? (
                                                                descriptions.length > 0 ? (
                                                                    <DescriptionBulletList 
                                                                        descriptions={descriptions}
                                                                        maxItems={3}
                                                                        showExpandable={true}
                                                                        areaId={theme.id}
                                                                        expandedState={expandedTheme === theme.id ? { [theme.id]: true } : {}}
                                                                        onToggle={() => handleThemeClick(theme.id)}
                                                                    />
                                                                ) : (
                                                                    displayNestedData(theme.description)
                                                                )
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                                                    No description available
                                                                </Typography>
                                                            )}
                                                        </AccordionDetails>
                                                    </Accordion>
                                                </motion.div>
                                            );
                                        })}
                                    </Box>
                                )}
                            </Box>
                        </Fade>
                    )}
                </Container>
            </Box>

            {/* Add Research Theme Dialog */}
            <Dialog 
                open={openDialog} 
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
                    }
                }}
            >
                <DialogTitle sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderBottom: 1,
                    borderColor: 'divider'
                }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={1}>
                            <AddIcon />
                            <Typography variant="h6" component="span">
                                Add New Research Theme
                            </Typography>
                        </Box>
                        <IconButton
                            edge="end"
                            color="inherit"
                            onClick={handleCloseDialog}
                            disabled={submitting}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ p: 3 }}>
                    <Box sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Research Theme Name *"
                                    value={themeName}
                                    onChange={(e) => setThemeName(e.target.value)}
                                    error={!!dialogErrors.themeName}
                                    helperText={dialogErrors.themeName}
                                    placeholder="Enter research theme name"
                                    variant="outlined"
                                    InputProps={{
                                        startAdornment: <CategoryIcon color="action" sx={{ mr: 1 }} />,
                                    }}
                                />
                            </Grid>
                            
                            {loggedInUser && (
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Creator Name"
                                        value={loggedInUser.name}
                                        disabled
                                        helperText="This is automatically set from your logged-in account"
                                        InputProps={{
                                            startAdornment: <PersonIcon color="action" sx={{ mr: 1 }} />,
                                            readOnly: true,
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                backgroundColor: '#f5f5f5',
                                            }
                                        }}
                                    />
                                </Grid>
                            )}
                            
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                                    Description Points *
                                </Typography>
                                {dialogErrors.description && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {dialogErrors.description}
                                    </Alert>
                                )}
                                <Paper sx={{ p: 2, border: `1px solid ${alpha('#667eea', 0.2)}`, borderRadius: 2 }}>
                                    {descriptionInputs.map((input, index) => (
                                        <Box key={index} sx={{ mb: 2, p: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                                            <Box display="flex" alignItems="flex-start" gap={1}>
                                                <FiberManualRecord 
                                                    sx={{ 
                                                        fontSize: '12px', 
                                                        color: 'primary.main',
                                                        mt: 1.5
                                                    }} 
                                                />
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    label={`Point ${index + 1}`}
                                                    value={input.data}
                                                    onChange={(e) => handleDescriptionChange(index, e.target.value)}
                                                    placeholder="Enter description point..."
                                                    variant="outlined"
                                                    size="small"
                                                />
                                                {descriptionInputs.length > 1 && (
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleRemoveDescription(index)}
                                                        sx={{ mt: 1 }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </Box>
                                    ))}
                                    
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<AddIcon />}
                                        onClick={handleAddDescription}
                                        fullWidth
                                        sx={{ mt: 1 }}
                                    >
                                        Add Another Point
                                    </Button>

                                    {/* Preview Section */}
                                    {descriptionInputs.some(input => input.data.trim()) && (
                                        <Paper sx={{ p: 2, mt: 2, bgcolor: alpha('#4caf50', 0.05), borderRadius: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom sx={{ color: 'success.main' }}>
                                                Preview:
                                            </Typography>
                                            <DescriptionBulletList 
                                                descriptions={descriptionInputs.filter(input => input.data.trim()).map(input => input.data)}
                                            />
                                        </Paper>
                                    )}
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                
                <DialogActions sx={{ p: 3, gap: 1 }}>
                    <Button
                        onClick={handleCloseDialog}
                        variant="outlined"
                        color="inherit"
                        startIcon={<CancelIcon />}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    
                    <Button
                        onClick={handleSubmitTheme}
                        variant="contained"
                        color="primary"
                        startIcon={submitting ? <CircularProgress size={20} /> : <AddIcon />}
                        disabled={submitting}
                        sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #5a67d8 0%, #6b46a0 100%)',
                            }
                        }}
                    >
                        {submitting ? 'Adding...' : 'Add Research Theme'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ResearchThemes;